import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaSpinner } from "react-icons/fa";
import { toast } from "react-toastify";
import { handlePostRequest } from "./hooks/api";
import { isAuthenticated } from "./utils/helpers";
import { successToast, errorToast } from "./utils/toasters";

export default function SigninPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [otpMethod, setOtpMethod] = useState("sms");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  // Extract returnUrl from query parameters
  const urlParams = new URLSearchParams(location.search);
  const returnUrl = urlParams.get("returnUrl");

  useEffect(() => {
    // Store return URL if provided
    if (returnUrl) {
      localStorage.setItem("redirectAfterLogin", decodeURIComponent(returnUrl));
    }

    if (isAuthenticated()) {
      const redirectPath = localStorage.getItem("redirectAfterLogin");

      if (redirectPath) {
        localStorage.removeItem("redirectAfterLogin");
        navigate(redirectPath);
      } else {
        navigate("/home");
      }
      return;
    }
  }, [returnUrl]);

  const handleSignin = async () => {
    if (!phoneNumber.trim()) {
      toast.error("Please enter a mobile number", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
      });
      return;
    }

    setIsProcessing(true);
    let formattedPhoneNumber = phoneNumber;
    if (!phoneNumber.startsWith("+977")) {
      formattedPhoneNumber = `+1${phoneNumber}`;
    }

    const payload = { phoneNumber: formattedPhoneNumber, otpMethod };
    try {
      const otpResponse = await handlePostRequest(
        "/auth/request-otp",
        payload,
        {},
        false
      );

      localStorage.setItem("phoneNumber", formattedPhoneNumber);
      localStorage.setItem("otpMethod", otpMethod);
      window.dispatchEvent(new Event("storage"));

      // Check if user came from group creation and show appropriate message
      const pendingGroupCreation = localStorage.getItem("pendingGroupCreation");
      const hasReturnUrl = localStorage.getItem("redirectAfterLogin");

      if (
        pendingGroupCreation ||
        (hasReturnUrl && hasReturnUrl.includes("groups/create"))
      ) {
        successToast(
          "OTP sent! After verification, we'll continue with your group creation."
        );
      } else {
        // Show OTP sent message for normal login
        successToast(
          `OTP sent to ${formattedPhoneNumber} via ${otpMethod.toUpperCase()}!`
        );
      }

      navigate("/otpverification");
    } catch (error) {
      toast.error(
        "" + (error.response?.data?.message ?? error.data?.message ?? error),
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
        }
      );
    } finally {
      localStorage.removeItem("loggedout");
      setIsProcessing(false);
    }
  };

  // Check if user was redirected from group creation
  const isFromGroupCreation = returnUrl && returnUrl.includes("groups/create");

  return (
    <>
      <div
        className="min-h-screen flex items-center justify-center p-4 font-afacad bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url(/images/loginbg.png)",
        }}
      >
        <div className="bg-white/95 backdrop-blur-sm shadow-lg p-4 sm:p-8 w-full max-w-xl rounded-lg">
          <div className="bg-[#FCFCFC]/90 backdrop-blur-sm rounded-2xl p-6 sm:p-10">
            <div className="relative">
              {/* Show message if user was redirected from create group */}
              {isFromGroupCreation && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                      <svg
                        className="w-4 h-4 text-blue-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-blue-800 text-sm font-medium">
                        Please login to continue creating your group
                      </p>
                      <p className="text-blue-600 text-xs mt-1">
                        Your form data has been saved and will be restored after
                        login
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col mb-8">
                <div className="flex items-center mb-6">
                  <img
                    src="/images/ping-desi-logo.png"
                    alt="Decoration"
                    className="h-6 w-6 mr-2"
                  />
                </div>
                <div className="flex flex-col items-start">
                  <img
                    src="/rickshaw.png"
                    alt="Rickshaw Logo"
                    className="h-20 mb-4"
                  />
                  <h1 className="text-2xl font-semibold text-black">
                    {isFromGroupCreation
                      ? "Login to Continue"
                      : "Welcome to Gallyo"}
                  </h1>
                  {isFromGroupCreation && (
                    <p className="text-sm text-gray-600 mt-1">
                      Complete your group creation after verification
                    </p>
                  )}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-bold text-black mb-2">
                  MOBILE NUMBER*
                </label>
                <div className="flex rounded-md shadow-sm">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                    (+1)
                  </span>
                  <input
                    type="tel"
                    className="flex-1 min-w-0 block w-full px-3 py-3 rounded-none rounded-r-md border border-gray-300 focus:ring-purple-700 focus:border-purple-700 text-sm"
                    placeholder="Enter mobile number"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                  />
                </div>
              </div>

              <div className="mb-10">
                <label className="block text-sm font-semibold text-black mb-2">
                  SEND OTP VIA*
                </label>
                <div className="flex flex-wrap gap-3">
                  <button
                    className={`flex items-center px-4 py-2 rounded-full text-sm ${
                      otpMethod === "sms"
                        ? "bg-[#f2e8f5] text-[#7B189F] border border-[#7B189F]"
                        : "bg-gray-100 text-purple-700 border border-transparent hover:border-purple-700"
                    }`}
                    onClick={() => setOtpMethod("sms")}
                  >
                    <img
                      src="/sms.png"
                      alt="SMS Icon"
                      className="h-4 w-4 mr-2"
                    />
                    SMS
                  </button>
                  <button
                    className={`flex items-center px-4 py-2 rounded-full text-sm ${
                      otpMethod === "whatsapp"
                        ? "bg-[#f2e8f5] text-[#7B189F] border border-[#7B189F]"
                        : "bg-gray-100 text-purple-700 border border-transparent hover:border-purple-700"
                    }`}
                    onClick={() => setOtpMethod("whatsapp")}
                  >
                    <img
                      src="/whatsapp.png"
                      alt="WhatsApp Icon"
                      className="h-4 w-4 mr-2"
                    />
                    WhatsApp
                  </button>
                </div>
              </div>

              <p className="text-xs text-black mt-6 mb-6 text-center">
                By Clicking Continue, I accept the{" "}
                <a href="/toc" className="text-[#7B189F] underline">
                  terms of services
                </a>{" "}
                and{" "}
                <a href="/privacy" className="text-[#7B189F] underline">
                  privacy policy
                </a>
              </p>

              <button
                onClick={handleSignin}
                disabled={isProcessing || phoneNumber.length < 1}
                className={`w-full font-semibold py-3 px-4 rounded-md focus:outline-none focus:shadow-outline flex items-center justify-center
                ${
                  isProcessing || phoneNumber.length < 1
                    ? "bg-purple-400 cursor-not-allowed text-gray-200"
                    : "bg-[#7b189fc6] hover:bg-[#7B189F] text-white"
                }`}
              >
                {isProcessing ? (
                  <FaSpinner className="animate-spin" />
                ) : isFromGroupCreation ? (
                  "Continue to Group Creation"
                ) : (
                  "Continue"
                )}
              </button>

              <div className="mt-4">
                <button
                  onClick={() => navigate("/")}
                  className="w-full py-2.5 rounded-md bg-[#7B189F] hover:bg-[#6a1589] text-white font-medium text-sm flex items-center justify-center transition-colors"
                >
                  Navigate to Home
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
